import type { FunnelReportsDashboardData } from './types';

export type FunnelReportDataSectionId =
  | 'active-placements'
  | 'funnel-health'
  | 'screen-flow'
  | 'payments'
  | 'paid-acquisition'
  | 'metric-timeseries'
  | 'anomaly-inbox'
  | 'root-cause'
  | 'artifacts'
  | 'data-quality';

export type FunnelReportAvailability = Record<FunnelReportDataSectionId, boolean>;

export const FUNNEL_REPORT_DATA_SECTION_IDS: FunnelReportDataSectionId[] = [
  'active-placements',
  'funnel-health',
  'screen-flow',
  'payments',
  'paid-acquisition',
  'metric-timeseries',
  'anomaly-inbox',
  'root-cause',
  'artifacts',
  'data-quality',
];

export function getFunnelReportAvailability(data: FunnelReportsDashboardData | null | undefined): FunnelReportAvailability {
  return {
    'active-placements': Boolean(rowCount(data?.aliasSummaries) || rowCount(data?.aliasOnboardingSummaries)),
    'funnel-health': hasFunnelHealthData(data),
    'screen-flow': Boolean(rowCount(data?.screenFlowEdges)),
    payments: Boolean(rowCount(data?.paymentOutcomes)),
    'paid-acquisition': Boolean(rowCount(data?.campaignEconomics)),
    'metric-timeseries': Boolean(rowCount(data?.metricTimeseries)),
    'anomaly-inbox': Boolean(rowCount(data?.anomalies)),
    'root-cause': Boolean(rowCount(data?.rootCauseDrivers)),
    artifacts: Boolean(rowCount(data?.artifacts)),
    'data-quality': hasDataQualityData(data),
  };
}

export function hasAnyFunnelReportData(data: FunnelReportsDashboardData | null | undefined): boolean {
  const availability = getFunnelReportAvailability(data);
  return FUNNEL_REPORT_DATA_SECTION_IDS.some(id => availability[id]);
}

function hasFunnelHealthData(data: FunnelReportsDashboardData | null | undefined): boolean {
  if (!data) return false;
  if (rowCount(data.funnelHealthDaily) > 0) return true;
  const summary = data.summary;
  return [
    summary.startedJourneys,
    summary.paywallReachedJourneys,
    summary.buyButtonClicks,
    summary.offerConvertedJourneys,
    summary.trialStartedJourneys,
    summary.purchaseJourneys,
    summary.purchaseAmount,
  ].some(value => (value ?? 0) > 0);
}

function hasDataQualityData(data: FunnelReportsDashboardData | null | undefined): boolean {
  if (!data) return false;
  if (rowCount(data.dataQuality) > 0) return true;
  return data.summary.minHealthScore !== null || Boolean(data.summary.latestMaterializedAt);
}

function rowCount(value: unknown): number {
  return Array.isArray(value) ? value.length : 0;
}

/**
 * Role visibility (`.design/studio-reports/VISIBILITY_SPEC.md`, owner round 5,
 * 2026-10-07). Everything on the Reports page that is DIAGNOSIS is platform-admin
 * only; what stays for every user is the three answers a decision is made from —
 * what the funnel did (the gates), which version carries that result, and whether
 * paid spend pays back.
 *
 * `admin` = `useAuth().isAdmin`. A project OWNER is not an admin here (decision 2):
 * the owner keeps manual refresh, not the diagnosis sections.
 */
export type FunnelReportViewerRole = 'admin' | 'member';

/**
 * Blocks beyond the ten data sections that the same role gate owns. Kept in one
 * union so a reviewer sees the whole surface, not a conditional per component.
 * `needs-decision` does not exist in code yet (owner call 07.10) — it is listed
 * so the block cannot ship ungated.
 */
export type FunnelReportRoleGatedBlockId =
  | FunnelReportDataSectionId
  | 'ab-test-decisions'
  | 'attention-strip'
  | 'warnings'
  | 'audience'
  | 'anchor-nav'
  | 'needs-decision';

/**
 * An ALLOWLIST on purpose: a block added later is admin-only until it is named
 * here, so a new diagnosis surface cannot leak by being forgotten.
 */
const MEMBER_VISIBLE_BLOCK_IDS: readonly FunnelReportRoleGatedBlockId[] = [
  'active-placements',
  'paid-acquisition',
];

/** Documentation-grade mirror of the allowlist — the spec's per-block verdict. */
export const VISIBLE_BLOCKS_BY_ROLE: Record<FunnelReportViewerRole, readonly FunnelReportRoleGatedBlockId[]> = {
  admin: [
    ...FUNNEL_REPORT_DATA_SECTION_IDS,
    'ab-test-decisions',
    'attention-strip',
    'warnings',
    'audience',
    'anchor-nav',
    'needs-decision',
  ],
  member: MEMBER_VISIBLE_BLOCK_IDS,
};

export function viewerRoleFromIsAdmin(isAdmin: boolean | undefined): FunnelReportViewerRole {
  return isAdmin ? 'admin' : 'member';
}

/** The single role predicate. Nothing on the page decides visibility by role elsewhere. */
export function isReportBlockVisibleToRole(
  id: FunnelReportRoleGatedBlockId,
  role: FunnelReportViewerRole,
): boolean {
  return role === 'admin' || MEMBER_VISIBLE_BLOCK_IDS.includes(id);
}

/**
 * Role BEFORE availability (VISIBILITY_SPEC implementation note): a hidden section
 * is dropped where availability already drops it, so the "showing N sections"
 * counter, the section nav, the empty-state text and `sectionErrorById` all stay
 * consistent and none of them can mention a section the reader cannot see.
 */
export function filterAvailabilityByRole(
  availability: FunnelReportAvailability,
  role: FunnelReportViewerRole,
): FunnelReportAvailability {
  if (role === 'admin') return availability;
  return FUNNEL_REPORT_DATA_SECTION_IDS.reduce((acc, id) => {
    acc[id] = availability[id] && isReportBlockVisibleToRole(id, role);
    return acc;
  }, {} as FunnelReportAvailability);
}
